<script>
import { mapState, mapActions, mapGetters } from 'vuex';
import TransportationTableInputs from '../../components/transportation/TransportationTableInputs';
import TransportationExtraAdd from '../../components/transportation/TransportationExtraAdd';
import TransportationExtraEdit from '../../components/transportation/TransportationExtraEdit';
import ConfirmDialog from '../../components/TwConfirmDialog';

export default {
  name: 'TransportationExtras',
  components: {
    TransportationTableInputs,
    TransportationExtraAdd,
    TransportationExtraEdit,
    ConfirmDialog,
  },
  data() {
    return {
      pagination: {
        rowsPerPage: 20,
      },
      paginationRowsPerPage: [
        20,
        50,
        { text: 'All', value: -1 },
      ],
      addShow: false,
      editShow: false,
      changesMade: false,
    };
  },
  computed: {
    ...mapState({
      extra: state => state.transportation.extras.extra,
      extras: state => state.transportation.extras.items,
      itemsError: state => state.transportation.extras.itemsError,
      itemsLoading: state => state.transportation.extras.itemsLoading,
      search: state => state.transportation.extras.search,
      saveError: state => state.transportation.extras.saveError,
      saveLoading: state => state.transportation.extras.saveLoading,
      lastRoute: state => state.transportation.extras.route,
      lastSupplier: state => state.transportation.extras.supplier,
      route: state => state.transportation.extras.route,
      routesLoading: state => state.transportation.routes.itemsLoading,
      supplier: state => state.transportation.suppliers.supplier,
      suppliers: state => state.transportation.suppliers.items,
      suppliersLoading: state => state.transportation.suppliers.itemsLoading,
    }),
    ...mapGetters({
      getRoutes: 'transportation/getRoutes',
      headers: 'transportation/getExtraHeaders',
    }),
  },
  methods: {
    ...mapActions({
      routesLoad: 'transportation/routesLoad',
      setExtrasSearch: 'transportation/setExtrasSearch',
      setActiveExtra: 'transportation/setActiveExtra',
      setExtraRoute: 'transportation/setExtraRoute',
    }),
    loadRoutes(force = false) {
      if (force || (this.supplier && this.supplier !== this.lastSupplier)) {
        this.routesLoad().then(() => {
          if (this.getRoutes.length) {
            this.setExtraRoute(this.getRoutes[0].id);
          }
        });
      }
    },
    openAddExtra() {
      this.addShow = true;
    },
    closeAddConfirm(force = false) {
      if (!force) {
        this.$refs.addConfirm.open();
      } else {
        this.addShow = false;
      }
    },
    openEditExtra(extra) {
      if (!this.extra || this.extra.id !== extra.id) {
        this.setActiveExtra(extra);
      }
      this.editShow = true;
    },
    closeEditConfirm(force = false) {
      if (this.changesMade && !force) {
        this.$refs.confirm.open();
      } else {
        this.editShow = false;
      }
    },
  },
  created() {
    this.loadRoutes();
  },
};
</script>

<template>
  <v-container fluid>
    <v-layout>
      <v-flex xs6>
        <transportation-table-inputs
          @change="loadRoutes"
          @search="setExtrasSearch"
          :disabled="itemsLoading"
          :search="search"
        />
      </v-flex>
      <v-flex class="text-xs-right" xs3 offset-xs3>
        <v-btn
          @click="openAddExtra"
          :disabled="!supplier || !route"
          color="primary"
        >
          Add new extra
        </v-btn>
      </v-flex>
    </v-layout>
    <v-layout>
      <v-flex xs3>
        <v-autocomplete
          @change="setExtraRoute"
          :items="getRoutes"
          :loading="suppliersLoading || routesLoading"
          :disabled="suppliersLoading || routesLoading"
          :value="route"
          label="Routes"
          item-text="name"
          item-value="id"
        />
      </v-flex>
    </v-layout>
    <v-layout mt-3 row>
      <v-flex>
        <v-data-table
          :items="extras"
          :headers="headers"
          :loading="itemsLoading"
          :pagination.sync="pagination"
          :rows-per-page-items="paginationRowsPerPage"
          :search="search"
          no-results-text="No results found"
          no-data-text="No extras were found for the supplier and route"
          must-sort
        >
          <template slot="items" slot-scope="props">
            <tr
              :class="{ 'blue lighten-5': extra && props.item.id === extra.id }"
            >
              <td class="px-2 pr-4">{{ props.item.id }}</td>
              <td class="px-2">{{ props.item.name }}</td>
              <td class="px-2">{{ props.item.webName }}</td>
              <td clas="px-2">{{ props.item.personCategoryName }}</td>
              <td class="px-2">{{ props.item.externalRef }}</td>
              <td class="px-2">
                <v-icon v-if="props.item.active" color="green">done</v-icon>
                <v-icon v-else color="red">close</v-icon>
              </td>
              <td class="px-2">
                <v-icon @click="openEditExtra(props.item)" class="mr-2" small>
                  edit
                </v-icon>
              </td>
            </tr>
          </template>
          <template slot="no-data">
            <div v-if="itemsError">
              <v-alert :value="true" icon="warning">
                An error occurred while loading.
                <span class="refresh-text">
                  Try again?
                </span>
              </v-alert>
            </div>
            <div v-else-if="!supplier" class="text-xs-center">
              <span>Choose a supplier to load routes</span>
            </div>
            <div v-else-if="!itemsLoading" class="text-xs-center">
              <span>No extras were found for the supplier and route</span>
            </div>
            <div v-else>
              <!--Show nothing when loading-->
            </div>
          </template>
        </v-data-table>
      </v-flex>

      <v-dialog
        v-model="editShow"
        @keydown.esc="editShow = false"
        max-width="600px"
        no-click-animation
        persistent
      >
        <transportation-extra-edit
          v-model="changesMade"
          @close="closeEditConfirm"
          :shown="editShow"
        />
      </v-dialog>

      <v-dialog
        v-model="addShow"
        @keydown.esc="addShow = false"
        max-width="600px"
        no-click-animation
        persistent
      >
        <transportation-extra-add
          @close="closeAddConfirm"
          :shown="addShow"
        />
      </v-dialog>

      <confirm-dialog
        @confirm="addShow = false"
        title="Cancel add extra"
        description="Are you sure you want to cancel adding an new extra?"
        ref="addConfirm"
      />

      <confirm-dialog
        @confirm="editShow = false"
        title="Are you sure?"
        description="There are changes that are not saved"
        ref="confirm"
      />
    </v-layout>
  </v-container>
</template>

<style type="scss">
  .v-messages {
    min-height: 14px !important;
  }
</style>



// WEBPACK FOOTER //
// src/pages/transportation/TransportationExtras.vue